import { useMutation } from "@apollo/client";
import Upload from "@mui/icons-material/Upload";
import { Button, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import EncampAutocomplete from "components/Forms/EncampAutocomplete";
import {
  isWastePipelineName,
  WASTE_PIPELINE_NAMES,
  WASTE_PIPELINE_REGISTRY,
  WastePipelineName,
} from "encamp-shared/src/pipelines";
import { gql } from "generated-graphql";
import { useCallback, useRef, useState } from "react";
import { uploadFileDirectly } from "util/uploadFile";

const DISPATCH_WASTE_PIPELINE_RUN_MUTATION = gql(`
  mutation DispatchWastePipelineRun($input: DispatchWastePipelineRunInput!) {
    dispatchWastePipelineRun(input: $input) {
      id
      status
      task
    }
  }
`);

const SEARCH_TENANTS_QUERY = gql(`
  query DispatchPipelineSearchTenants($search: String, $pageSize: Int, $sort: [SortModel!]) {
    tenants(search: $search, pageSize: $pageSize, sort: $sort) {
      items {
        id
        name
      }
      count
    }
  }
`);

type TenantOption = { id: string; name: string };

const PIPELINE_OPTIONS = WASTE_PIPELINE_NAMES.map((name) => ({
  label: WASTE_PIPELINE_REGISTRY[name].label,
  value: name,
}));

function getFixedTenant(name: WastePipelineName): TenantOption | null {
  return WASTE_PIPELINE_REGISTRY[name].fixedTenant;
}

function hasAdditionalFile(name: WastePipelineName): boolean {
  return WASTE_PIPELINE_REGISTRY[name].hasAdditionalFile === true;
}

interface DispatchWastePipelineRunDialogProps {
  onClose: () => void;
}

export function DispatchWastePipelineRunDialog({ onClose }: DispatchWastePipelineRunDialogProps) {
  const alerts = useAlerts();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const additionalFileInputRef = useRef<HTMLInputElement>(null);

  const [pipelineName, setPipelineName] = useState<WastePipelineName>("clean-harbors");
  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(null);
  const [tenantSearch, setTenantSearch] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [additionalFile, setAdditionalFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [dispatchMutation] = useMutation(DISPATCH_WASTE_PIPELINE_RUN_MUTATION);

  const fixedTenant = getFixedTenant(pipelineName);
  const effectiveTenant = fixedTenant ?? selectedTenant;
  const showAdditionalFile = hasAdditionalFile(pipelineName);
  const canSubmit = pipelineName && effectiveTenant && file && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!effectiveTenant || !file) return;

    setSubmitting(true);
    try {
      const sourceS3Key = await uploadFileDirectly(file, {
        tenantId: effectiveTenant.id,
      });

      let additionalSourceS3Key: string | undefined;
      if (additionalFile) {
        additionalSourceS3Key = await uploadFileDirectly(additionalFile, {
          tenantId: effectiveTenant.id,
        });
      }

      const { data } = await dispatchMutation({
        variables: {
          input: {
            pipelineName,
            tenantId: effectiveTenant.id,
            sourceS3Key,
            additionalSourceS3Key,
          },
        },
      });

      const jobId = data?.dispatchWastePipelineRun.id;
      alerts.success(`Pipeline dispatched successfully. Job ID: ${jobId}`);
      onClose();
    } catch (err) {
      alerts.error(
        `Failed to dispatch pipeline: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setSubmitting(false);
    }
  }, [effectiveTenant, file, additionalFile, pipelineName, dispatchMutation, alerts, onClose]);

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const selected = event.target.files?.[0];
    if (selected) {
      setFile(selected);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAdditionalFileChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const selected = event.target.files?.[0];
    if (selected) {
      setAdditionalFile(selected);
    }
    if (additionalFileInputRef.current) {
      additionalFileInputRef.current.value = "";
    }
  };

  const handlePipelineChange = (value: WastePipelineName) => {
    setPipelineName(value);
    // Clear tenant selection when switching to/from a fixed-tenant pipeline
    if (getFixedTenant(value)) {
      setSelectedTenant(null);
    }
    // Clear additional file when switching to a pipeline that doesn't support it
    if (!hasAdditionalFile(value)) {
      setAdditionalFile(null);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      title="New Pipeline Run"
      primaryActionButtonProps={{
        onClick: handleSubmit,
        saveText: "Dispatch",
        loading: submitting,
        disabled: !canSubmit,
      }}
    >
      <Stack spacing={3} sx={{ pt: 1 }}>
        <TextField
          select
          label="Pipeline Type"
          value={pipelineName}
          onChange={(e) => {
            const val = e.target.value;
            if (isWastePipelineName(val)) {
              handlePipelineChange(val);
            }
          }}
          fullWidth
        >
          {PIPELINE_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </TextField>

        {fixedTenant ? (
          <TextField label="Organization" value={fixedTenant.name} disabled fullWidth />
        ) : (
          <EncampAutocomplete
            query={SEARCH_TENANTS_QUERY}
            getItems={(data) => data?.tenants.items ?? []}
            getOptionLabel={(option) => option.name}
            variables={{
              search: tenantSearch,
              pageSize: 25,
              sort: [{ field: "name", sort: "asc" as const }],
            }}
            value={selectedTenant}
            onChange={(value) => setSelectedTenant(value)}
            onInputChange={(value) => setTenantSearch(value)}
            label="Organization"
          />
        )}

        <Stack direction="row" alignItems="center" spacing={2}>
          <Button variant="outlined" component="label" startIcon={<Upload />}>
            {showAdditionalFile ? "Biennial Data" : "Choose File"}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              hidden
            />
          </Button>
          {file && (
            <Typography variant="body2" color="text.secondary">
              {file.name}
            </Typography>
          )}
        </Stack>

        {showAdditionalFile && (
          <Stack direction="row" alignItems="center" spacing={2}>
            <Button variant="outlined" component="label" startIcon={<Upload />}>
              Monthly Data (Optional)
              <input
                ref={additionalFileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleAdditionalFileChange}
                hidden
              />
            </Button>
            {additionalFile && (
              <Typography variant="body2" color="text.secondary">
                {additionalFile.name}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Dialog>
  );
}
